import React, { createContext, useContext } from 'react'
import { createAsset, deleteAsset, getVariant, presignAsset } from '@cimpress-technology/sherbert-sdk'
import md5 from 'md5'

interface AssetStoreContextType {
  uploadAsset: (file: File) => Promise<{ publicUrl: string; fileName: string; name: string; id: string }>
  deleteAssetById: (assetId: string) => Promise<void>
}

const AssetStoreContext = createContext<AssetStoreContextType | null>(null)

interface AssetStoreProviderProps {
  children: React.ReactNode
  auth?: {
    getAuthorizationHeader?: () => string
  }
}

export const AssetStoreProvider: React.FC<AssetStoreProviderProps> = ({ children, auth }) => {
  const renameFile = (fileName: string) => {
    const dot = fileName.lastIndexOf('.')
    const name = fileName.substring(0, dot).toLowerCase()
    const ext = fileName.substring(dot, fileName.length).toLowerCase()
    return md5(name + Date.now()) + ext
  }

  const uploadAsset = async (file: File): Promise<{ publicUrl: string; fileName: string; name: string; id: string }> => {
    const fileName = renameFile(file.name)
    try {
      const asset = await createAsset({
        body: {
          file,
          expires: 'P2Y',
          tenant: 'JqzyMexb118PYH7BJBqKY',
          info: {
            asset: {
              hidden: true,
            },
          },
        },
        authorizationHeader: () => auth?.getAuthorizationHeader?.() || '',
      })

      const signature = await presignAsset({
        assetId: asset.id,
        authorizationHeader: () => auth?.getAuthorizationHeader?.() || '',
      })

      const { variantUrl } = await getVariant({
        variants: [{ variant: 'original' }],
        assetId: asset.id,
        assetVersion: asset.version,
        signature: signature.signature,
      })
      return { publicUrl: variantUrl, fileName, name: file.name, id: asset.id }
    } catch (error) {
      console.error('Error uploading asset:', error)
      return {
        publicUrl: `File attachment upload failed: ${file.name}`,
        fileName,
        name: file.name,
        id: '',
      }
    }
  }

  const deleteAssetById = async (assetId: string) => {
    if (!assetId) {
      console.error('no file to delete')
      return
    }
    try {
      await deleteAsset({
        assetId,
        authorizationHeader: () => auth?.getAuthorizationHeader?.() || '',
      })
    } catch {
      console.error(' error while deleting file')
    }
  }

  const contextValue: AssetStoreContextType = {
    uploadAsset,
    deleteAssetById,
  }

  return <AssetStoreContext.Provider value={contextValue}>{children}</AssetStoreContext.Provider>
}

export const useAssetStore = (): AssetStoreContextType | null => {
  return useContext(AssetStoreContext)
}
